import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, Loader2, Package } from "lucide-react";
import { Button } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useHomeSearch } from "@/hooks";
import { useCurrency } from "@/hooks/currency-context";
import { formatPrice, SetSrc } from "@/utils";
import type { ISearchProduct } from "@/hooks/useHomeSearch";
import { trackSearch, trackSelectSearchResult } from "@/utils/analytics";

type NavbarSearchProps = {
  triggerClassName?: string;
};

// Custom hook for debouncing
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

// Custom hook for keyboard shortcuts
const useKeyboardShortcut = (
  key: string,
  callback: () => void,
  enabled: boolean,
) => {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === key) callback();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [key, callback, enabled]);
};

// Custom hook for body scroll lock
const useBodyScrollLock = (locked: boolean) => {
  useEffect(() => {
    document.body.style.overflow = locked ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [locked]);
};

export const NavbarSearch = ({ triggerClassName = "" }: NavbarSearchProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { currency } = useCurrency();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced query using custom hook
  const debouncedQuery = useDebounce(query.trim(), 300);
  const enabled = debouncedQuery.length >= 1;

  const {
    data: products,
    isLoading,
    isFetching,
  } = useHomeSearch({
    query: debouncedQuery,
    enabled,
  });

  const showLoading =
    enabled && (isLoading || isFetching) && (!products || products.length === 0);

  // Track search query execution
  useEffect(() => {
    if (enabled && products && !isLoading && !isFetching) {
      trackSearch(debouncedQuery, products.length);
    }
  }, [debouncedQuery, products, isLoading, isFetching, enabled]);

  // Memoized close handler
  const handleClose = useCallback(() => setIsOpen(false), []);

  // Custom hooks for side effects
  useKeyboardShortcut("Escape", handleClose, isOpen);
  useBodyScrollLock(isOpen);

  // Focus input when opened, reset state when closed
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    } else {
      setQuery("");
    }
  }, [isOpen]);

  // Clear input handler
  const handleClearInput = useCallback(() => {
    setQuery("");
    inputRef.current?.focus();
  }, []);

  return (
    <>
      <Button
        isIconOnly
        aria-label={t("KEY.SEARCH_ARIA_LABEL")}
        variant="bordered"
        className={`size-9 lg:size-10 rounded-lg ${triggerClassName}`}
        onPress={() => setIsOpen(true)}
      >
        <Search className="size-4" />
      </Button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed h-screen inset-0 bg-elegantBlack/50 backdrop-blur-sm z-50"
              onClick={handleClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.97 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="fixed top-[10vh] left-1/2 -translate-x-1/2 w-[min(520px,92vw)] max-h-[75vh] bg-mainBg border border-border-2 rounded-2xl shadow-2xl overflow-hidden z-50 flex flex-col"
            >
              {/* Search input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border-2">
                <Search className="size-5 text-thirdText shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("KEY.SEARCH_PLACEHOLDER")}
                  className="flex-1 bg-transparent text-base text-primaryText placeholder:text-thirdText outline-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={handleClearInput}
                    className="p-1 rounded-full hover:bg-border-2 transition-colors"
                    aria-label={t("KEY.CLEAR_SEARCH")}
                  >
                    <X className="size-4 text-thirdText" />
                  </button>
                )}
                {(isLoading || isFetching) && enabled && (
                  <Loader2 className="size-4 text-secondary animate-spin shrink-0" />
                )}
              </div>

              {/* Results */}
              <div className="flex-1 overflow-y-auto search-scrollbar min-h-[200px]">
                {/* Initial state */}
                {!enabled && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 py-12 text-center"
                  >
                    <Search className="size-10 text-thirdText/30 mx-auto mb-3" />
                    <p className="text-sm text-thirdText">
                      {t("KEY.SEARCH_PLACEHOLDER")}
                    </p>
                  </motion.div>
                )}

                {/* Loading skeleton */}
                {enabled && showLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col"
                  >
                    {[...Array(4)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.08, duration: 0.3 }}
                        className="flex items-center gap-3 px-4 py-3 border-b border-border-2/50 last:border-b-0"
                      >
                        <div className="size-14 shrink-0 rounded-xl bg-border-2/50 animate-pulse" />
                        <div className="flex-1 flex flex-col gap-2">
                          <div className="h-3.5 w-3/4 rounded-md bg-border-2/50 animate-pulse" />
                          <div className="h-3 w-1/2 rounded-md bg-border-2/30 animate-pulse" />
                          <div className="h-3 w-1/3 rounded-md bg-border-2/40 animate-pulse" />
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {/* No results - Enhanced Creative UI */}
                {enabled && !showLoading && (!products || products.length === 0) && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="px-6 py-10 flex flex-col items-center"
                  >
                    {/* Animated illustration */}
                    <div className="relative mb-6">
                      {/* Floating particles */}
                      {[...Array(5)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute size-2 rounded-full bg-secondary/20"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{
                            opacity: [0, 1, 0],
                            scale: [0.5, 1, 0.5],
                            x: [0, (i % 2 === 0 ? 1 : -1) * (20 + i * 8)],
                            y: [0, -30 - i * 10],
                          }}
                          transition={{
                            duration: 2,
                            delay: i * 0.2,
                            repeat: Infinity,
                            repeatDelay: 1,
                          }}
                          style={{
                            left: "50%",
                            top: "50%",
                          }}
                        />
                      ))}

                      {/* Main icon container with glow effect */}
                      <motion.div
                        animate={{
                          scale: [1, 1.02, 1],
                          rotate: [0, 1, -1, 0],
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="relative"
                      >
                        {/* Glow ring */}
                        <motion.div
                          animate={{
                            opacity: [0.3, 0.6, 0.3],
                            scale: [1, 1.1, 1],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                          className="absolute inset-0 rounded-3xl bg-linear-to-br from-secondary/20 to-transparent blur-xl"
                        />

                        {/* Icon box */}
                        <div className="relative size-24 rounded-3xl bg-linear-to-br from-border-2/80 to-border-2/40 flex items-center justify-center shadow-lg border border-border-2/50">
                          <motion.div
                            animate={{ y: [0, -4, 0] }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          >
                            <Package
                              className="size-10 text-thirdText/50"
                              strokeWidth={1.5}
                            />
                          </motion.div>

                          {/* Search indicator */}
                          <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{
                              delay: 0.3,
                              type: "spring",
                              stiffness: 200,
                            }}
                            className="absolute -bottom-2 -right-2 size-10 rounded-xl bg-mainBg border-2 border-border-2 flex items-center justify-center shadow-md"
                          >
                            <Search className="size-5 text-thirdText/60" />
                            <motion.div
                              initial={{ pathLength: 0 }}
                              animate={{ pathLength: 1 }}
                              className="absolute inset-0 flex items-center justify-center"
                            >
                              <X className="size-2 text-primary/70 absolute" />
                            </motion.div>
                          </motion.div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Text content */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                      className="text-center space-y-2 mb-5"
                    >
                      <h3 className="text-base font-semibold text-primaryText">
                        {t("KEY.SEARCH_NO_RESULTS")}
                      </h3>
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-border-2/50 mt-2">
                        <Search className="size-3 text-thirdText/60" />
                        <span className="text-xs text-thirdText font-medium truncate max-w-[180px]">
                          &ldquo;{debouncedQuery}&rdquo;
                        </span>
                      </div>
                    </motion.div>
                  </motion.div>
                )}

                {/* Results list */}
                {products && products.length > 0 && (
                  <>
                    <AnimatePresence mode="popLayout">
                      {products.map((p, i) => (
                        <motion.div
                          key={p.id}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{
                            duration: 0.25,
                            delay: Math.min(i * 0.04, 0.3),
                            ease: "easeOut",
                          }}
                        >
                          <SearchResultItem
                            product={p}
                            locale={locale}
                            currency={currency}
                            onClick={() => {
                              trackSelectSearchResult(debouncedQuery, {
                                id: p.slug ?? p.id,
                                name: p.name,
                                category: p.category?.name,
                              });
                              handleClose();
                            }}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </>
                )}
              </div>

              {/* Close button */}
              <div className="px-4 py-3 border-t border-border-2 flex justify-center">
                <Button
                  size="sm"
                  variant="light"
                  onPress={handleClose}
                  startContent={<X className="size-4" />}
                  className="text-thirdText"
                >
                  {t("KEY.CLOSE")}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

const SearchResultItem = ({
  product,
  locale,
  currency,
  onClick,
}: {
  product: ISearchProduct;
  locale: string;
  currency: any;
  onClick: () => void;
}) => {
  const name = product.name;
  const categoryName = product.category?.name;
  const price = parseFloat(product.price || "0");
  const discountPrice = parseFloat(product.sale_price || "0");
  const hasDiscount = discountPrice > 0 && discountPrice < price;
  const displayPrice = hasDiscount ? discountPrice : price;
  const [imgSrc, setImgSrc] = useState(SetSrc(product.primary_image));
  const [hasErrored, setHasErrored] = useState(false);

  return (
    <Link
      to={`/product/${product.slug ?? product.id}`}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 hover:bg-border-2/40 transition-colors border-b border-border-2/50 last:border-b-0"
    >
      <div className="relative size-14 shrink-0 rounded-xl overflow-hidden bg-border-2/30">
        <img
          src={imgSrc}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => {
            if (!hasErrored) {
              setHasErrored(true);
              setImgSrc("/images/logo.png");
            }
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-primaryText line-clamp-1">
          {name}
        </p>
        {categoryName && (
          <p className="text-xs text-thirdText mt-0.5 line-clamp-1">
            {categoryName}
          </p>
        )}
        {price > 0 && (
          <div className="flex items-center gap-1.5 mt-1">
            {hasDiscount && (
              <span className="text-xs text-thirdText line-through">
                {formatPrice(price, currency, locale)}
              </span>
            )}
            <span className="text-sm font-semibold text-secondary">
              {formatPrice(displayPrice, currency, locale)}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};
